/**
 * Per-language overlays for portfolio projects.
 * EN canonical lives in portfolioProjects.js; DE/ES/AR overlays here.
 * `localizeProject(project, lang)` merges the overlay over the EN project.
 * titleAccent MUST be a literal suffix of title.
 * `captions` maps by index onto project.essay; `numbers` maps labels by index.
 * `credits` keeps the EN label keys (translated by t() in ProjectPage) with localized values.
 */

const OVERLAYS = {
  DE: {
    'octapharma-eu-congress': {
      title: 'Octapharma EU-Kongress.',
      titleAccent: 'EU-Kongress.',
      eyebrowDate: 'MANDARIN ORIENTAL · FRÜHJAHR 2024',
      brief: 'Dreitägiges Plenum plus achtzehn Breakouts und eine Abschlussgala für Europas Hämatologie-Führung, mit mehrsprachiger Simultanübersetzung.',
      sub: 'Ein dreitägiges europäisches Hämatologie-Treffen, neu gebaut um Klarheit, Sprachzugang und eine Abschlussgala, die mit Reden endete, nicht mit Service.',
      briefBody: 'Octapharma bat uns, die europäische Hämatologie-Führung drei Tage lang in Istanbul zu empfangen, ohne den Agenturnebel, der Kongresse dieser Größe gewöhnlich umgibt. Gewünscht waren ein einziger Ansprechpartner, eine Bühne, die über vier Sprachen funktioniert, und ein Bogen, der mit einer Gala endet, für die der Vorstand wirklich bleiben will.',
      challenge: 'Die Herausforderung war Choreografie. Achtzehn Breakouts in einer Venue bedeuten achtzehn Raumwechsel, drei parallele Lunch-Services und ein Plenum, das nie mehr als vier Minuten Verspätung haben darf, sonst brechen Übersetzerkabinen, Screens und Seitentür-Schlangen gleichzeitig. Wir mussten den Tag entwerfen, nicht nur buchen.',
      intent: 'Wir behandelten den Kongress wie ein einziges Theaterstück. Die Plenarbühne war auf Klarheit dimensioniert, nicht auf Spektakel, flaches Licht auf den Speakern, Line-Array-Sound für die Rückwand kalibriert, und ein Übersetzerkabinen-Raster mit Sicht auf jedes Gesicht im Saal. Die Gala fand in einem separaten Saal statt, um den Registerwechsel zu markieren: Kerzenlicht, niedrigere Decke, langsamere Musik, weniger Logos. Derselbe Captain führte beide Räume.',
      captions: [
        'Plenum, zwölf Minuten vor Einlass.',
        'Ein Speaker liest die Rückwand, nicht die Leinwand.',
        'Breakout vier, Stunden vor dem Lunch.',
        'Der Ablaufplan auf dem Pult des Captains.',
        'Istanbul, der weite Blick, für den die Delegierten anreisten.',
        'Eine Gala, die den Saal hielt, ohne die Stimme zu heben.',
        'Eine Tafel, gedeckt für die, die zuletzt sprechen.',
        'Ein Glas, das vom Briefing bis in die Nacht reiste.',
      ],
      numbers: ['Delegierte', 'Breakout-Räume', 'NPS-Wert'],
      credits: {
        Venue: 'Mandarin Oriental Bosphorus',
        Catering: 'Mandarin Oriental, hauseigen',
        AV: 'Audeon Pro Istanbul',
        Florals: 'Aksel Atelier',
        Photography: 'Folgt in Kürze',
        Scenography: 'GlowMeets, hauseigen',
      },
    },
    'muslim-hands-annual': {
      title: 'Muslim Hands Jahrestagung.',
      titleAccent: 'Jahrestagung.',
      eyebrowDate: 'HILTON BOSPHORUS · HERBST 2024',
      brief: 'Plenum, Workshop und Kulturabend an drei Spielstätten, in zwei Sprachen.',
      sub: 'Die jährliche Strategietagung einer britischen Stiftung, geführt über drei Istanbuler Spielstätten mit zwei Arbeitssprachen und einem einzigen Delivery-Captain.',
      briefBody: 'Muslim Hands hält seine jährliche Führungstagung einmal im Jahr ab und setzt damit die Programmentscheidungen der nächsten zwölf Monate. Gewünscht waren drei Formate in einer Reise, ein Arbeitsplenum, strukturierte Workshops und ein Kulturabend, ohne dass eines der drei wie ein Nebenprogramm der anderen wirkt.',
      challenge: 'Das härtere Problem war nicht die Agenda, es waren die Wechsel zwischen den Spielstätten. Dreihundert Teilnehmer an drei Istanbuler Adressen bedeuten drei Transferfenster, in denen Pünktlichkeit entscheidet, ob die nächste Session rechtzeitig beginnt. Wir entwarfen zuerst das Timing, dann die Venues.',
      intent: 'Jede Venue wurde für das gewählt, was ihr Raum am besten kann. Der Hilton-Ballsaal trug das Plenum, weil er mit dreihundert Menschen akustisch ruhig bleibt. Der Workshop-Raum in Tophane gab den Kleingruppen bewusst eine andere Temperatur, niedrigere Decke, Tageslicht, keine Mikrofone. Der Kulturabend zog auf eine Bosporus-Terrasse, damit der Tag am Wasser endete, nicht im Hotelkorridor. Der Captain fuhr jeden Transfer mit.',
      captions: [
        'Plenum, gestellt für klare Sicht aus der letzten Reihe.',
        'Workshops, absichtlich ohne Mikrofone.',
        'Ein Captain-Pult zwischen zwei Venues.',
        'Die Route zwischen drei Istanbuler Adressen.',
        'Kulturabend, das Wasser macht die Arbeit.',
        'Eine Tafel, die den Tag beendete, nicht definierte.',
        'Stille Hospitality, bewusst unterinszeniert.',
        'Licht, warm genug gehalten, um darüber zu sprechen.',
      ],
      numbers: ['Teilnehmer', 'Venues', 'Erneut gebucht'],
      credits: {
        Venue: 'Hilton Istanbul Bosphorus + Tophane Kulturzentrum',
        Catering: 'Hilton hauseigen + Asitane (Kulturabend)',
        AV: 'Audeon Pro Istanbul',
        Florals: 'Folgt in Kürze',
        Photography: 'Folgt in Kürze',
        Scenography: 'GlowMeets, hauseigen',
      },
    },
    'messe-stuttgart-inbound': {
      title: 'Messe Stuttgart Inbound.',
      titleAccent: 'Inbound.',
      eyebrowDate: 'ISTANBUL & BURSA · WINTER 2024',
      brief: 'Acht kuratierte technische Standortbesuche in zwei Städten, entworfen um Partnerschaftsgespräche.',
      sub: 'Eine deutsche Industriedelegation, geführt durch Istanbul und Bursa, mit einem Ziel: Partnerschaftsgespräche, die in Unterschriften endeten.',
      briefBody: 'Messe Stuttgart wollte 180 leitende Industriedelegierte für eine Woche in die Türkei bringen, mit dem expliziten Ziel, mit unterzeichneten Memoranden abzureisen. Gebraucht wurden Zugang zu Herstellern, Behörden und Logistikpartnern, und ein Programm, das deutschen Pragmatismus und türkische Gastfreundschaft in denselben Raum bringt, ohne eines von beiden zu verwässern.',
      challenge: 'Die Herausforderung war Glaubwürdigkeit auf beiden Seiten. Die deutsche Delegation brauchte Werkshallen-Zugang nach pünktlichen Zeitplänen. Die türkischen Partner brauchten Kultur- und Protokollbetreuung, die ihre Seniorität respektiert. Wir mussten Standortbesuche entwerfen, die operativ wirkten, nicht zeremoniell, und einen Hospitality-Bogen, der jeden Tag auf der richtigen Note schloss.',
      intent: 'Wir teilten jeden Tag in zwei Hälften. Vormittags technisch: Werksbegehungen in Bursa, Regulierungsbriefings in Istanbul, Partner-Pitches in Boardrooms. Nachmittags relational: kuratierte Lunches, Spaziergänge über die historische Halbinsel, abends ein Dinner im vom Partner empfohlenen Restaurant statt im Hotelbankettsaal. Die Übersetzer arbeiteten alle zwölf Stunden. Am Donnerstag entwarf der Raum bereits MoUs.',
      captions: [
        'Die Halbinsel, zwischen den Meetings.',
        'Ein Werkshallen-Briefing in Bursa.',
        'Boardroom eins, am Vormittag.',
        'Partner-Pitches mit zwei Sprachen live.',
        'Dinner, wo der Tag wirklich endete.',
        'Eine Tafel, gedeckt zum Weiterreden.',
        'Abschlussdinner der Inbound-Woche.',
        'Ein Stift, gereicht über einen Entwurf.',
      ],
      numbers: ['Delegierte', 'Standortbesuche', 'MoUs unterzeichnet'],
      credits: {
        Venue: 'Verschiedene, Boardrooms, Fabriken, Partner-Restaurants',
        Catering: 'Kuratiert je Venue',
        AV: 'Audeon Pro Istanbul',
        Florals: 'n/a',
        Photography: 'Folgt in Kürze',
        Scenography: 'GlowMeets, hauseigen',
      },
    },
    'liya-island-anniversary': {
      title: 'Liya Island Jubiläum.',
      titleAccent: 'Jubiläum.',
      eyebrowDate: 'LIYA ISLAND · JUNI 2024',
      brief: 'Marken-Jubiläumsabend mit mehrgängigem Dinner, Kerzenlicht-Szenografie an der Bucht und einer Überraschungs-Drohnenshow zum Abschluss.',
      sub: 'Ein Markenjubiläum, gebaut als ein einziger filmischer Abend an einer privaten Bucht, der Lichtplan war der Ablaufplan.',
      briefBody: 'Der Kunde wollte zwanzig Jahre an dem Tag feiern, an dem er zwanzig Jahre zuvor eröffnet hatte, und der Abend sollte sich anfühlen wie das Innere einer Erinnerung, nicht wie eine Firmenfeier. Gewünscht: eine Bucht, eine lange Tafel, keine Bühne, keine Podiumsreden. Ein Foto am Ende der Nacht, das auch in fünf Jahren noch trägt.',
      challenge: 'Die Herausforderung waren das Wetterfenster und die Stille. Liya Island hat einen einzigen Anlegesteg, Gäste, Lieferanten, AV und Catering mussten innerhalb eines vierstündigen Aufbaus durch dasselbe Tor. Und das Briefing verlangte nur Ambient-Musik, kein MC, keine Mikrofone, keine Durchsagen, also musste jedes Cue über Szenografie landen, nicht über Stimme.',
      intent: 'Wir entwarfen die Nacht als drei Lichtzustände. Die Ankunft war warm und niedrig, Laternen säumten den Weg vom Steg, Kerzenlicht auf der langen Tafel, die Bucht absichtlich dunkel. Das Dinner hielt diese Tonart zwei Stunden. Dann, exakt um 22:48, leuchtete die Bucht auf: 180 Drohnen flogen neunzig Sekunden lang das Markenzeichen über das Wasser, und verschwanden. Die After-Party begann in ihrem Nachglühen, an derselben Bucht, ohne Übergangsrede.',
      captions: [
        'Die lange Tafel, Stunden bevor Gäste den Steg betraten.',
        'Ein Gedeck, gemacht, um aus der Nähe fotografiert zu werden.',
        'Kerzenlicht, gestimmt auf einen bestimmten Bildausschnitt.',
        'Die Bucht, absichtlich dunkel gehalten, bis 22:48.',
        'Neunzig Sekunden Markenzeichen über dem Wasser.',
        'Dessert, gegessen, ohne dass es jemand ankündigte.',
        'Ein Glas, erhoben auf nichts Bestimmtes.',
        'After-Party, beginnend, wo das Dinner endete.',
      ],
      numbers: ['Gäste', 'Bucht', 'Blieben zur After-Party'],
      credits: {
        Venue: 'Liya Island, private Bucht',
        Catering: 'Von GlowMeets kuratiertes Küchenteam',
        AV: 'Audeon Pro Istanbul',
        Florals: 'Aksel Atelier',
        Photography: 'Folgt in Kürze',
        Scenography: 'GlowMeets, hauseigen',
      },
    },
    'ciragan-palace-ceremony': {
      title: 'Çırağan Palace Zeremonie.',
      titleAccent: 'Zeremonie.',
      eyebrowDate: 'ÇIRAĞAN PALACE KEMPINSKI · JUNI 2024',
      brief: 'Kaiserliche Bosporus-Terrassen-Zeremonie und dreitägiger Bogen mit mehrsprachigem Trauredner und Gästen aus elf Ländern.',
      sub: 'Eine dreitägige Bosporus-Hochzeit für zwei Familien aus elf Ländern, verankert in einer einzigen Terrassen-Zeremonie zur goldenen Stunde.',
      briefBody: 'Die beiden Familien hatten die Hochzeit zwei Jahre lang geplant. Gewünscht waren eine Zeremonie auf der Kaiserterrasse des Çırağan Palace, ein mehrsprachiger Trauredner, der Englisch, Türkisch und Arabisch ohne Rhythmusbruch trägt, und ein dreitägiger Bogen, der Gästen von außerhalb der Türkei Zeit gab, die Stadt vor der Zeremonie wirklich zu sehen.',
      challenge: 'Die Herausforderung war die Terrasse selbst. Sie ist sechzehn Meter tief, mit einer harten Windlinie ab der siebten Reihe, sobald der Bosporus nach Sonnenuntergang auffrischt. Bestuhlung, Ton und Florales mussten um 18:30 in stiller Luft sauber lesen und um 20:15 noch halten, wenn der Wind querschlägt. Und jedes Protokolldetail musste zwei kulturelle Prüfungen bestehen, nicht eine.',
      intent: 'Wir behandelten die Zeremonie als Zentrum einer längeren Komposition. Tag eins war das Welcome-Dinner drinnen, entspannt, ohne Protokoll, mit zweisprachigem MC. Tag zwei war die Zeremonie selbst, terminiert auf exakt 19:42, damit die Eröffnungszeile des Trauredners im Licht der goldenen Stunde landete, mit Floralarchitektur, dimensioniert als Windschutz, nicht als Dekoration. Tag drei: Bosporus-Brunch und Stadttour für die internationalen Gäste. Derselbe Captain hielt alle drei Tage.',
      captions: [
        'Ein erstes Foto, privat gehalten bis zum Auszug.',
        'Die Terrasse um 19:38, vier Minuten vor den Gelübden.',
        'Florales, gebaut um den Wind zu brechen, nicht das Foto.',
        'Der Empfang, drinnen gedeckt, eine Stunde vor den Gästen.',
        'Das Wasser, zwischen Zeremonie und Dinner.',
        'Reden, die mit Absicht kurz waren.',
        'Eine lange Tafel, die bewusst an der Bar vorbeilief.',
        'Letztes Licht von Tag drei.',
      ],
      numbers: ['Gäste', 'Länder', 'Tage-Bogen'],
      credits: {
        Venue: 'Çırağan Palace Kempinski',
        Catering: 'Çırağan Palace, hauseigen',
        AV: 'Audeon Pro Istanbul',
        Florals: 'Aksel Atelier',
        Photography: 'Folgt in Kürze',
        Scenography: 'GlowMeets, hauseigen',
      },
    },
    'argos-in-cappadocia': {
      title: 'Argos in Kappadokien.',
      titleAccent: 'Kappadokien.',
      eyebrowDate: 'ARGOS IN CAPPADOCIA · OKTOBER 2024',
      brief: 'Welcome-Dinner in Höhlen, Ballonfotografie bei Sonnenaufgang, Empfang im Tal, über drei Nächte.',
      sub: 'Eine Hochzeit über drei Nächte, getaktet nach dem Rhythmus Kappadokiens, Höhlen in der Dämmerung, Ballons bei Sonnenaufgang, das Tal im Zwielicht.',
      briefBody: 'Das Paar wollte Kappadokien nicht als Kulisse, sondern als Dramaturgie der Hochzeit selbst. Wir sollten die Tage um das natürliche Licht der Landschaft programmieren: die Höhlen von Argos für das Welcome-Dinner, das Tal bei Sonnenaufgang für die Fotografie, die offene Terrasse für den Empfang, wenn die Felsen bernsteinfarben werden.',
      challenge: 'Die harte Kante war Logistik. Kappadokien bedeutet verstreute Venues, schmale Talstraßen und ein Sonnenaufgangs-Fotografiefenster von zwanzig Minuten, nicht verhandelbar. Dazu fünfundneunzig Gäste aus vier Städten und ein Heißluftballonflug, der von einer Windmessung drei Stunden vor dem Start abhängt. Nichts davon verzeiht Improvisation.',
      intent: 'Wir bauten die Hochzeit um das Licht, nicht um den Grundriss. Das Welcome-Dinner lag tief im Höhlenrestaurant von Argos, sodass die Decke das Kerzenlicht trug, nicht Blumenbögen. Die Zeremonie selbst war ein kleiner, bewusster Moment auf der Terrasse zur goldenen Stunde, unter dreißig Minuten gehalten. Der Empfang breitete sich ins Tal aus, als die Felsen weich wurden. Tag drei öffnete mit einem Start um 05:14, Gäste im Korb, das Paar in einem zweiten Ballon, die Fotografie aus einem dritten. Der ganze Bogen passt in exakt drei Nächte.',
      captions: [
        'Sonnenaufgang, der Moment, als die Windmessung zurückkam.',
        'Das Tal, zwischen Zeremonie und Dinner.',
        'Gelübde auf einer Terrasse, die keine Florale brauchte.',
        'Welcome-Dinner im Höhlenrestaurant.',
        'Eine Tafel, gedeckt, wo das Kerzenlicht einmal sprang.',
        'Das Paar, bevor der zweite Ballon abhob.',
        'Empfang, als die Felsen bernsteinfarben wurden.',
        'Die letzte Nacht der drei.',
      ],
      numbers: ['Gäste', 'Nächte', 'Erneute Türkei-Buchungen'],
      credits: {
        Venue: 'Argos in Cappadocia',
        Catering: 'Seki Restaurant, Argos, hauseigen',
        AV: 'Audeon Pro Kappadokien-Partner',
        Florals: 'Aksel Atelier Reiseteam',
        Photography: 'Folgt in Kürze',
        Scenography: 'GlowMeets, hauseigen',
      },
    },
  },

  ES: {
    'octapharma-eu-congress': {
      title: 'Congreso Europeo de Octapharma.',
      titleAccent: 'de Octapharma.',
      eyebrowDate: 'MANDARIN ORIENTAL · PRIMAVERA 2024',
      brief: 'Plenario de tres días más dieciocho breakouts y una gala de clausura para los líderes europeos de hematología, con interpretación simultánea multilingüe.',
      sub: 'Un encuentro europeo de hematología de tres días, reconstruido en torno a la claridad, el acceso lingüístico y una gala que terminó con discursos, no con servicio.',
      briefBody: 'Octapharma nos pidió recibir a su cúpula europea de hematología durante tres días en Estambul, sin la neblina de agencia que suele rodear a congresos de esta escala. Querían un único punto de contacto, un escenario que funcionara en cuatro idiomas y un arco que terminara con una gala en la que su consejo quisiera realmente quedarse.',
      challenge: 'El desafío era la coreografía. Dieciocho breakouts en una sola sede significan dieciocho cambios de sala, tres servicios de almuerzo en pistas paralelas y un plenario que nunca puede retrasarse más de cuatro minutos, o cada cabina de traducción, pantalla y cola lateral se fracturan a la vez. Teníamos que diseñar el día, no solo reservarlo.',
      intent: 'Tratamos el congreso como una sola pieza de teatro. El escenario plenario se dimensionó para la claridad, no el espectáculo: luz plana sobre los ponentes, sonido line-array calibrado para la pared del fondo y una retícula de cabinas de traducción con vista a cada rostro de la sala. La gala se montó en un salón aparte para marcar el cambio de registro: velas, techo más bajo, música más lenta, menos logos. El mismo capitán dirigió ambas salas.',
      captions: [
        'El plenario, doce minutos antes de abrir puertas.',
        'Un ponente leyendo la pared del fondo, no la pantalla.',
        'Breakout cuatro, horas antes de que llegara el almuerzo.',
        'El guion sobre la mesa del capitán.',
        'Estambul, la vista larga por la que volaron los delegados.',
        'Una gala que sostuvo la sala sin levantar la voz.',
        'Una mesa puesta para quienes hablan al final.',
        'Una copa que viajó del encargo a la noche.',
      ],
      numbers: ['Delegados', 'Salas de breakout', 'Puntuación NPS'],
      credits: {
        Venue: 'Mandarin Oriental Bosphorus',
        Catering: 'Mandarin Oriental, propio',
        AV: 'Audeon Pro Istanbul',
        Florals: 'Aksel Atelier',
        Photography: 'Próximamente',
        Scenography: 'GlowMeets, propio',
      },
    },
    'muslim-hands-annual': {
      title: 'Anual de Muslim Hands.',
      titleAccent: 'Muslim Hands.',
      eyebrowDate: 'HILTON BOSPHORUS · OTOÑO 2024',
      brief: 'Programa mixto de plenario, talleres y velada cultural en tres sedes, en dos idiomas.',
      sub: 'La reunión estratégica anual de una organización británica, dirigida en tres sedes de Estambul con dos idiomas de trabajo y un solo capitán de entrega.',
      briefBody: 'Muslim Hands celebra su encuentro anual de liderazgo una vez al año y lo usa para fijar las decisiones de programa de los próximos doce meses. Pidieron tres formatos en un solo viaje, un plenario de trabajo, talleres estructurados y una velada cultural, sin que ninguno de los tres pareciera una actividad secundaria de los otros.',
      challenge: 'El problema más difícil no era la agenda: era el traslado entre sedes. Trescientos asistentes en tres direcciones de Estambul son tres ventanas de transferencia donde la puntualidad decide si la siguiente sesión empieza a tiempo. Diseñamos primero los tiempos y después las sedes.',
      intent: 'Cada sede se eligió por lo que su sala hace mejor. El salón del Hilton albergó el plenario porque se mantiene acústicamente sereno con trescientas personas dentro. El espacio de talleres de Tophane dio a las sesiones pequeñas otra temperatura a propósito: techo bajo, luz natural, sin micrófonos. La velada cultural salió a una terraza del Bósforo para que el día terminara sobre el agua, no en un pasillo de hotel. El capitán acompañó cada traslado.',
      captions: [
        'Plenario montado para leerse limpio desde la última fila.',
        'Talleres sin micrófonos, a propósito.',
        'La mesa de un capitán entre dos sedes.',
        'La ruta entre tres direcciones de Estambul.',
        'Velada cultural: el agua haciendo el trabajo.',
        'Una mesa que cerró el día sin definirlo.',
        'Hospitalidad silenciosa, deliberadamente contenida.',
        'Luz mantenida lo bastante cálida para conversar.',
      ],
      numbers: ['Asistentes', 'Sedes', 'Recontratado'],
      credits: {
        Venue: 'Hilton Istanbul Bosphorus + Centro Cultural de Tophane',
        Catering: 'Hilton propio + Asitane (velada cultural)',
        AV: 'Audeon Pro Istanbul',
        Florals: 'Próximamente',
        Photography: 'Próximamente',
        Scenography: 'GlowMeets, propio',
      },
    },
    'messe-stuttgart-inbound': {
      title: 'Messe Stuttgart Inbound.',
      titleAccent: 'Inbound.',
      eyebrowDate: 'ESTAMBUL Y BURSA · INVIERNO 2024',
      brief: 'Ocho visitas técnicas curadas en dos ciudades, diseñadas en torno a conversaciones de colaboración.',
      sub: 'Una delegación industrial alemana guiada por Estambul y Bursa con un solo propósito: conversaciones de colaboración que terminaran en firmas.',
      briefBody: 'Messe Stuttgart quería traer a 180 de sus delegados industriales sénior a Türkiye durante una semana, con el objetivo explícito de marcharse con memorandos firmados. Necesitaban acceso a fabricantes, reguladores y socios logísticos, y un programa que pusiera el pragmatismo alemán y la hospitalidad turca en la misma sala sin diluir ninguno de los dos.',
      challenge: 'El desafío era la credibilidad en ambos lados. La delegación alemana necesitaba acceso a planta con horarios puntuales. Las contrapartes turcas necesitaban una cobertura cultural y de protocolo que respetara su jerarquía. Tuvimos que diseñar visitas que se sintieran operativas, no ceremoniales, y un arco de hospitalidad que cerrara cada día en la nota correcta.',
      intent: 'Dividimos cada día en dos mitades. Las mañanas eran técnicas: recorridos de fábrica en Bursa, sesiones regulatorias en Estambul, presentaciones de socios en salas de juntas. Las tardes eran relacionales: almuerzos curados, paseos por la península histórica, una cena en un restaurante sugerido por los socios en lugar de un salón de banquetes. Los traductores trabajaron las doce horas. El jueves, la sala ya redactaba memorandos.',
      captions: [
        'La península, entre reuniones.',
        'Una sesión a pie de fábrica en Bursa.',
        'Sala de juntas uno, a media mañana.',
        'Presentaciones de socios con dos idiomas en vivo.',
        'La cena donde el día terminaba de verdad.',
        'Una mesa puesta para seguir conversando.',
        'Cena de clausura de la semana inbound.',
        'Un bolígrafo pasado sobre un borrador.',
      ],
      numbers: ['Delegados', 'Visitas técnicas', 'Memorandos firmados'],
      credits: {
        Venue: 'Varios, salas de juntas, fábricas, restaurantes de socios',
        Catering: 'Curado por sede',
        AV: 'Audeon Pro Istanbul',
        Florals: 'n/a',
        Photography: 'Próximamente',
        Scenography: 'GlowMeets, propio',
      },
    },
    'liya-island-anniversary': {
      title: 'Aniversario en Liya Island.',
      titleAccent: 'Liya Island.',
      eyebrowDate: 'LIYA ISLAND · JUNIO 2024',
      brief: 'Velada de aniversario de marca con cena de varios tiempos, escenografía a la luz de las velas en la cala y un espectáculo sorpresa de drones cerrando la noche.',
      sub: 'Un aniversario de marca construido como una sola velada cinematográfica en una cala privada, donde el plano de iluminación era el guion.',
      briefBody: 'El cliente quería celebrar veinte años el mismo día en que abrió, veinte años antes, y quería que la velada se sintiera como el interior de un recuerdo, no como una función corporativa. Pidieron una cala, una mesa larga, sin escenario, sin discursos de podio. Una fotografía al final de la noche que siguiera en pie dentro de cinco años.',
      challenge: 'El desafío era la ventana meteorológica y el silencio. Liya Island tiene un único embarcadero de acceso, así que invitados, proveedores, AV y catering pasaron por la misma puerta en un montaje de cuatro horas. Y el encargo pedía solo música ambiental, sin maestro de ceremonias, sin micrófonos, sin anuncios, de modo que cada señal debía llegar por la escenografía, no por la voz.',
      intent: 'Diseñamos la noche como tres estados de luz. La llegada fue cálida y baja: faroles bordeando el camino desde el embarcadero, velas en la mesa larga, la cala deliberadamente a oscuras. La cena sostuvo ese tono dos horas. Entonces, exactamente a las 22:48, la cala se encendió: 180 drones dibujaron la marca sobre el agua durante noventa segundos y desaparecieron. El after empezó en su estela, en la misma cala, sin discurso de transición. La sala mantuvo su propio ritmo.',
      captions: [
        'La mesa larga, horas antes de que los invitados pisaran el embarcadero.',
        'Un cubierto pensado para fotografiarse de cerca.',
        'Velas afinadas a un encuadre concreto.',
        'La cala, a oscuras a propósito, hasta las 22:48.',
        'Noventa segundos de la marca sobre el agua.',
        'El postre, comido sin que nadie lo anunciara.',
        'Una copa alzada a nada en particular.',
        'El after, empezando donde terminó la cena.',
      ],
      numbers: ['Invitados', 'Cala', 'Se quedaron al after'],
      credits: {
        Venue: 'Liya Island, cala privada',
        Catering: 'Equipo de chefs curado por GlowMeets',
        AV: 'Audeon Pro Istanbul',
        Florals: 'Aksel Atelier',
        Photography: 'Próximamente',
        Scenography: 'GlowMeets, propio',
      },
    },
    'ciragan-palace-ceremony': {
      title: 'Ceremonia en el Palacio Çırağan.',
      titleAccent: 'Palacio Çırağan.',
      eyebrowDate: 'ÇIRAĞAN PALACE KEMPINSKI · JUNIO 2024',
      brief: 'Ceremonia en la terraza imperial del Bósforo y arco de tres días con oficiante multilingüe e invitados de once países.',
      sub: 'Una boda de tres días en el Bósforo para dos familias de once países, anclada en una sola ceremonia en terraza a la hora dorada.',
      briefBody: 'Las dos familias llevaban dos años planeando la boda. Querían una ceremonia en la terraza imperial del Palacio Çırağan, un oficiante multilingüe capaz de sostener inglés, turco y árabe sin romper el ritmo, y un arco de tres días que diera a los invitados de fuera de Türkiye tiempo para ver de verdad la ciudad antes de la ceremonia.',
      challenge: 'El desafío era la propia terraza. Tiene dieciséis metros de fondo y una línea de viento dura en la séptima fila cuando el Bósforo arrecia tras la puesta de sol. Tuvimos que diseñar asientos, sonido y arquitectura floral que se leyeran limpios en aire quieto a las 18:30 y aguantaran a las 20:15 cuando cruzara el viento. Y cada detalle de protocolo debía superar dos revisiones culturales, no una.',
      intent: 'Tratamos la ceremonia como el centro de una composición más larga. El día uno fue la cena de bienvenida en interior: relajada, sin protocolo, con maestro de ceremonias bilingüe. El día dos fue la ceremonia, fijada para empezar exactamente a las 19:42 para que la primera línea del oficiante cayera bajo la luz dorada, con arquitectura floral dimensionada como cortaviento y no como decoración. El día tres, brunch en el Bósforo y tour por la ciudad para los invitados internacionales. El mismo capitán sostuvo los tres días.',
      captions: [
        'Una primera fotografía, guardada en privado hasta la salida.',
        'La terraza a las 19:38, cuatro minutos antes de los votos.',
        'Flores construidas para romper el viento, no la fotografía.',
        'La recepción montada, en interior, una hora antes de sentar a los invitados.',
        'El agua, entre la ceremonia y la cena.',
        'Discursos cortos, a propósito.',
        'Una mesa larga que pasaba de largo la barra, deliberadamente.',
        'Última luz del tercer día.',
      ],
      numbers: ['Invitados', 'Países', 'Días de arco'],
      credits: {
        Venue: 'Çırağan Palace Kempinski',
        Catering: 'Çırağan Palace, propio',
        AV: 'Audeon Pro Istanbul',
        Florals: 'Aksel Atelier',
        Photography: 'Próximamente',
        Scenography: 'GlowMeets, propio',
      },
    },
    'argos-in-cappadocia': {
      title: 'Argos in Cappadocia.',
      titleAccent: 'Cappadocia.',
      eyebrowDate: 'ARGOS IN CAPPADOCIA · OCTUBRE 2024',
      brief: 'Cena de bienvenida en cuevas, fotografía en globo al amanecer, recepción en el valle a lo largo de tres noches.',
      sub: 'Una boda de tres noches anclada al ritmo de Capadocia: cuevas al atardecer, globos al amanecer, el valle en el crepúsculo.',
      briefBody: 'La pareja quería Capadocia no como telón de fondo sino como la dramaturgia de la propia boda. Nos pidieron programar los días según la luz natural del paisaje: las cuevas de Argos para la cena de bienvenida, el valle al amanecer para la fotografía, la terraza abierta para la recepción cuando las rocas se vuelven ámbar.',
      challenge: 'El filo duro era la logística. Capadocia significa sedes dispersas, carreteras estrechas de valle y una ventana de fotografía al amanecer medida en veinte minutos, sin flexibilidad. Teníamos además noventa y cinco invitados llegando de cuatro ciudades y un vuelo en globo que depende de una lectura de viento tomada tres horas antes del despegue. Nada de esto tolera la improvisación.',
      intent: 'Construimos la boda alrededor de la luz, no del plano. La cena de bienvenida se montó en lo profundo del restaurante-cueva de Argos para que el techo cargara la luz de las velas, no arcos florales. La ceremonia fue un momento pequeño e intencional en la terraza a la hora dorada, de menos de treinta minutos. La recepción se extendió por el valle mientras las rocas se suavizaban hacia el ámbar. El tercer día abrió con un despegue a las 05:14, los invitados en la cesta, la pareja en un segundo globo, la fotografía desde un tercero. El arco completo cabe en tres noches, exactamente.',
      captions: [
        'Amanecer, el momento en que llegó la lectura del viento.',
        'El valle entre la ceremonia y la cena.',
        'Votos en una terraza que no necesitaba flores.',
        'Cena de bienvenida dentro del restaurante-cueva.',
        'Una mesa puesta donde la luz de las velas rebotó una vez.',
        'La pareja, antes de que despegara el segundo globo.',
        'La recepción, mientras las rocas se volvían ámbar.',
        'La última noche de las tres.',
      ],
      numbers: ['Invitados', 'Noches', 'Nuevos viajes a Türkiye'],
      credits: {
        Venue: 'Argos in Cappadocia',
        Catering: 'Restaurante Seki, propio de Argos',
        AV: 'Socio de Audeon Pro en Capadocia',
        Florals: 'Equipo itinerante de Aksel Atelier',
        Photography: 'Próximamente',
        Scenography: 'GlowMeets, propio',
      },
    },
  },

  AR: {
    'octapharma-eu-congress': {
      title: 'مؤتمر Octapharma الأوروبي.',
      titleAccent: 'الأوروبي.',
      eyebrowDate: 'ماندارين أورينتال · ربيع 2024',
      brief: 'جلسات عامة لثلاثة أيام مع ثماني عشرة جلسة فرعية وحفل ختامي لقادة أمراض الدم في أوروبا، مع ترجمة فورية متعددة اللغات.',
      sub: 'لقاء أوروبي لأمراض الدم على مدى ثلاثة أيام، أعيد بناؤه حول الوضوح وسهولة اللغة وحفل ختامي انتهى بالخطابات لا بالخدمة.',
      briefBody: 'طلبت منا Octapharma استضافة قيادتها الأوروبية لأمراض الدم ثلاثة أيام في إسطنبول دون ضبابية الوكالات التي تحيط عادة بمؤتمرات بهذا الحجم. أرادوا نقطة تواصل واحدة، ومسرحاً يعمل عبر أربع لغات، وقوساً ينتهي بحفل يرغب مجلس إدارتهم فعلاً في البقاء له.',
      challenge: 'كان التحدي في التنسيق الحركي. ثماني عشرة جلسة فرعية داخل مكان واحد تعني ثمانية عشر تحويلاً للقاعات، وثلاث خدمات غداء على مسارات متوازية، وجلسة عامة لا يجوز أن تتأخر أكثر من أربع دقائق, وإلا تصدعت كبائن الترجمة والشاشات وطوابير الأبواب الجانبية دفعة واحدة. كان علينا تصميم اليوم، لا مجرد حجزه.',
      intent: 'تعاملنا مع المؤتمر كعمل مسرحي واحد. صُمم مسرح الجلسات العامة للوضوح لا للإبهار, إضاءة مسطحة على المتحدثين، وصوت معاير للجدار الخلفي، وشبكة كبائن ترجمة موضوعة بحيث ترى كل وجه في القاعة. أقيم الحفل الختامي في قاعة منفصلة لتمييز تغيّر النبرة: ضوء شموع، وسقف أخفض، وموسيقى أبطأ، وشعارات أقل. والقائد نفسه أدار القاعتين.',
      captions: [
        'الجلسة العامة، قبل فتح الأبواب باثنتي عشرة دقيقة.',
        'متحدث يقرأ الجدار الخلفي لا الشاشة.',
        'الجلسة الفرعية الرابعة، قبل وصول الغداء بساعات.',
        'جدول الفقرات على طاولة القائد.',
        'إسطنبول, المشهد البعيد الذي سافر المندوبون لأجله.',
        'حفل أمسك بالقاعة دون أن يرفع صوته.',
        'مائدة أعدت لمن يتكلمون أخيراً.',
        'كأس سافرت من الموجز إلى الليل.',
      ],
      numbers: ['مندوباً', 'قاعة فرعية', 'نسبة الرضا'],
      credits: {
        Venue: 'ماندارين أورينتال البوسفور',
        Catering: 'ماندارين أورينتال, داخلي',
        AV: 'Audeon Pro إسطنبول',
        Florals: 'Aksel Atelier',
        Photography: 'قريباً',
        Scenography: 'GlowMeets, داخلي',
      },
    },
    'muslim-hands-annual': {
      title: 'لقاء Muslim Hands السنوي.',
      titleAccent: 'السنوي.',
      eyebrowDate: 'هيلتون البوسفور · خريف 2024',
      brief: 'برنامج يجمع الجلسات العامة وورش العمل وأمسية ثقافية في ثلاثة أماكن وبلغتين.',
      sub: 'اللقاء الاستراتيجي السنوي لمؤسسة بريطانية، أدير عبر ثلاثة أماكن في إسطنبول بلغتي عمل وقائد تنفيذ واحد.',
      briefBody: 'تعقد Muslim Hands لقاءها القيادي السنوي مرة في العام وتحدد به قرارات برامجها للأشهر الاثني عشر التالية. طلبوا ثلاثة أشكال في رحلة واحدة, جلسة عامة عملية، وورش عمل منظمة، وأمسية ثقافية, دون أن يبدو أي منها نشاطاً جانبياً للآخرين.',
      challenge: 'المشكلة الأصعب لم تكن الأجندة, بل الانتقال بين الأماكن. ثلاثمئة مشارك عبر ثلاثة عناوين في إسطنبول تعني ثلاث نوافذ نقل تقرر فيها الدقة ما إذا كانت الجلسة التالية ستبدأ في موعدها. صممنا التوقيت أولاً والأماكن ثانياً.',
      intent: 'اختير كل مكان لما تجيده قاعته. احتضنت قاعة هيلتون الجلسة العامة لأنها تبقى هادئة صوتياً بثلاثمئة شخص داخلها. ومنح فضاء توبهانه لورش العمل حرارة مختلفة عن قصد, سقف أخفض، وضوء نهار، ولا ميكروفونات. وانتقلت الأمسية الثقافية إلى شرفة على البوسفور لينتهي اليوم على الماء لا في ممر فندق. ورافق القائد كل تنقّل.',
      captions: [
        'الجلسة العامة، معدة لتُقرأ بوضوح من الصف الأخير.',
        'ورش عمل بلا ميكروفونات عن قصد.',
        'طاولة قائد بين مكانين.',
        'الطريق بين ثلاثة عناوين في إسطنبول.',
        'الأمسية الثقافية, الماء يقوم بالعمل.',
        'مائدة أنهت اليوم ولم تعرّفه.',
        'ضيافة هادئة، خافتة الإخراج عمداً.',
        'إضاءة أبقيت دافئة بما يكفي للحديث عبرها.',
      ],
      numbers: ['مشاركاً', 'أماكن', 'حُجز مجدداً'],
      credits: {
        Venue: 'هيلتون إسطنبول البوسفور + مركز توبهانه الثقافي',
        Catering: 'هيلتون داخلي + Asitane (الأمسية الثقافية)',
        AV: 'Audeon Pro إسطنبول',
        Florals: 'قريباً',
        Photography: 'قريباً',
        Scenography: 'GlowMeets, داخلي',
      },
    },
    'messe-stuttgart-inbound': {
      title: 'وفد Messe Stuttgart الوافد.',
      titleAccent: 'الوافد.',
      eyebrowDate: 'إسطنبول وبورصة · شتاء 2024',
      brief: 'ثماني زيارات تقنية منتقاة في مدينتين، صُممت حول محادثات الشراكة.',
      sub: 'وفد تجاري صناعي ألماني قُدناه عبر إسطنبول وبورصة بهدف واحد: محادثات شراكة تنتهي بالتوقيعات.',
      briefBody: 'أرادت Messe Stuttgart استقدام 180 من كبار مندوبيها الصناعيين إلى تركيا لأسبوع، بهدف صريح: المغادرة بمذكرات تفاهم موقعة. احتاجوا وصولاً إلى المصنعين والمنظمين وشركاء اللوجستيات, وبرنامجاً يضع البراغماتية الألمانية والضيافة التركية في قاعة واحدة دون أن يخفف أياً منهما.',
      challenge: 'كان التحدي هو المصداقية لدى الطرفين. احتاج الوفد الألماني وصولاً إلى أرض المصانع بجداول دقيقة. واحتاج النظراء الأتراك تغطية ثقافية وبروتوكولية تحترم مكانتهم. كان علينا تصميم زيارات تبدو تشغيلية لا احتفالية, وقوس ضيافة يختم كل يوم على النغمة الصحيحة.',
      intent: 'قسمنا كل يوم نصفين. الصباحات تقنية: جولات مصانع في بورصة، وإحاطات تنظيمية في إسطنبول، وعروض شركاء في قاعات اجتماعات. والأمسيات علائقية: غداءات منتقاة، وجولات مشي في شبه الجزيرة التاريخية، وعشاء في مطعم اقترحه الشركاء بدلاً من قاعة مآدب فندقية. عمل المترجمون الساعات الاثنتي عشرة كلها. وبحلول الخميس كانت القاعة تصوغ مذكرات التفاهم.',
      captions: [
        'شبه الجزيرة، بين الاجتماعات.',
        'إحاطة على أرض مصنع في بورصة.',
        'قاعة الاجتماعات الأولى، منتصف الصباح.',
        'عروض شركاء بلغتين مباشرة.',
        'العشاء حيث انتهى اليوم فعلاً.',
        'مائدة أعدت لمواصلة الحديث.',
        'عشاء ختام الأسبوع الوافد.',
        'قلم يُمرر فوق مسودة.',
      ],
      numbers: ['مندوباً', 'زيارات ميدانية', 'مذكرة تفاهم وُقعت'],
      credits: {
        Venue: 'أماكن متعددة, قاعات اجتماعات ومصانع ومطاعم شركاء',
        Catering: 'منتقى لكل مكان',
        AV: 'Audeon Pro إسطنبول',
        Florals: 'لا ينطبق',
        Photography: 'قريباً',
        Scenography: 'GlowMeets, داخلي',
      },
    },
    'liya-island-anniversary': {
      title: 'ذكرى تأسيس في جزيرة ليا.',
      titleAccent: 'جزيرة ليا.',
      eyebrowDate: 'جزيرة ليا · يونيو 2024',
      brief: 'أمسية ذكرى تأسيس علامة مع عشاء متعدد الأطباق ومشهدية شموع على الخليج وعرض درون مفاجئ يختم الليلة.',
      sub: 'ذكرى تأسيس علامة بُنيت كأمسية سينمائية واحدة على خليج خاص، حيث كانت خطة الإضاءة هي جدول الفقرات.',
      briefBody: 'أراد العميل الاحتفال بعشرين عاماً في اليوم نفسه الذي افتتح فيه قبل عشرين عاماً، وأراد للأمسية أن تشبه داخل ذكرى لا مناسبة شركات. طلبوا خليجاً واحداً، وطاولة واحدة طويلة، ولا مسرح، ولا خطابات منصة. وصورة في نهاية الليلة تصمد بعد خمس سنوات.',
      challenge: 'كان التحدي نافذة الطقس والصمت. لجزيرة ليا رصيف وصول واحد، ما يعني أن الضيوف والموردين والصوتيات والضيافة مروا جميعاً عبر البوابة نفسها خلال تجهيز من أربع ساعات. وطلب الموجز موسيقى محيطة فقط, لا مقدم، ولا ميكروفونات، ولا إعلانات, فكان على كل إشارة أن تصل عبر المشهدية لا الصوت.',
      intent: 'صممنا الليلة كثلاث حالات ضوئية. كان الوصول دافئاً منخفضاً, فوانيس تحف الطريق من الرصيف، وضوء شموع على الطاولة الطويلة، والخليج معتم عمداً. وحافظ العشاء على تلك النغمة ساعتين. ثم، في 22:48 تماماً، أضاء الخليج: 180 درون رسمت شعار العلامة فوق الماء تسعين ثانية ثم اختفت. وبدأ الحفل اللاحق في أثرها، على الخليج نفسه، دون خطاب انتقال. وحافظت القاعة على إيقاعها الخاص.',
      captions: [
        'الطاولة الطويلة، قبل أن يطأ الضيوف الرصيف بساعات.',
        'غطاء مائدة صُنع ليُصوَّر عن قرب.',
        'ضوء شموع مضبوط على إطار واحد بعينه.',
        'الخليج معتم عن قصد، حتى 22:48.',
        'تسعون ثانية من شعار العلامة فوق الماء.',
        'حلوى أكلت دون أن يعلنها أحد.',
        'كأس رفعت من غير مناسبة بعينها.',
        'الحفل اللاحق يبدأ حيث انتهى العشاء.',
      ],
      numbers: ['ضيفاً', 'خليج', 'بقوا للحفل اللاحق'],
      credits: {
        Venue: 'جزيرة ليا، خليج خاص',
        Catering: 'فريق طهاة منتقى من GlowMeets',
        AV: 'Audeon Pro إسطنبول',
        Florals: 'Aksel Atelier',
        Photography: 'قريباً',
        Scenography: 'GlowMeets, داخلي',
      },
    },
    'ciragan-palace-ceremony': {
      title: 'مراسم قصر تشيراغان.',
      titleAccent: 'تشيراغان.',
      eyebrowDate: 'قصر تشيراغان كمبينسكي · يونيو 2024',
      brief: 'مراسم على الشرفة الإمبراطورية للبوسفور وقوس من ثلاثة أيام مع مأذون متعدد اللغات وضيوف من إحدى عشرة دولة.',
      sub: 'عرس على البوسفور لثلاثة أيام لعائلتين من إحدى عشرة دولة، يرتكز على مراسم شرفة واحدة عند الساعة الذهبية.',
      briefBody: 'كانت العائلتان تخططان للعرس منذ عامين. أرادتا مراسم على الشرفة الإمبراطورية في قصر تشيراغان، ومأذوناً متعدد اللغات يحمل الإنجليزية والتركية والعربية دون أن ينكسر إيقاعه، وقوساً من ثلاثة أيام يمنح الضيوف القادمين من خارج تركيا وقتاً لرؤية المدينة فعلاً قبل المراسم.',
      challenge: 'كان التحدي هو الشرفة نفسها. عمقها ستة عشر متراً مع خط رياح حاد عند الصف السابع حين يشتد البوسفور بعد الغروب. كان علينا تصميم جلوس وصوت وعمارة زهور تُقرأ نظيفة في هواء ساكن عند 18:30 وتصمد عند 20:15 حين تعبر الرياح. وكان على كل تفصيل بروتوكولي أن يجتاز مراجعتين ثقافيتين لا واحدة.',
      intent: 'تعاملنا مع المراسم كمركز لتكوين أطول. اليوم الأول كان عشاء الترحيب داخلياً, مسترخياً، خارج البروتوكول، بمقدم ثنائي اللغة. واليوم الثاني كان المراسم نفسها، ضُبط بدؤها على 19:42 تماماً لتحط جملة المأذون الافتتاحية تحت ضوء الساعة الذهبية، مع عمارة زهور صُممت كمصدّ رياح لا كزينة. واليوم الثالث فطور متأخر على البوسفور وجولة في المدينة للضيوف الدوليين. والقائد نفسه أمسك بالأيام الثلاثة.',
      captions: [
        'صورة أولى، بقيت خاصة حتى موكب الخروج.',
        'الشرفة عند 19:38, قبل العهود بأربع دقائق.',
        'زهور بُنيت لتكسر الريح لا الصورة.',
        'الاستقبال جاهز، داخلياً، قبل جلوس الضيوف بساعة.',
        'الماء، بين المراسم والعشاء.',
        'خطابات قصيرة عن قصد.',
        'طاولة طويلة تجاوزت البار عمداً.',
        'آخر ضوء من اليوم الثالث.',
      ],
      numbers: ['ضيفاً', 'دولة', 'أيام القوس'],
      credits: {
        Venue: 'قصر تشيراغان كمبينسكي',
        Catering: 'قصر تشيراغان, داخلي',
        AV: 'Audeon Pro إسطنبول',
        Florals: 'Aksel Atelier',
        Photography: 'قريباً',
        Scenography: 'GlowMeets, داخلي',
      },
    },
    'argos-in-cappadocia': {
      title: 'أرغوس في كابادوكيا.',
      titleAccent: 'كابادوكيا.',
      eyebrowDate: 'Argos in Cappadocia · أكتوبر 2024',
      brief: 'عشاء ترحيب في الكهوف، وتصوير بالمناطيد عند الشروق، واستقبال في الوادي على مدى ثلاث ليالٍ.',
      sub: 'عرس من ثلاث ليالٍ مثبت على إيقاع كابادوكيا, الكهوف عند الغسق، والمناطيد عند الشروق، والوادي عند الشفق.',
      briefBody: 'أراد العروسان كابادوكيا لا كخلفية بل كدراما العرس نفسها. طلبا منا برمجة الأيام حول ضوء المكان الطبيعي: كهوف أرغوس لعشاء الترحيب، والوادي عند الشروق للتصوير، والشرفة المفتوحة للاستقبال حين تتحول الصخور إلى كهرمان.',
      challenge: 'الحافة الصعبة كانت اللوجستيات. كابادوكيا تعني أماكن متفرقة، وطرق وديان ضيقة، ونافذة تصوير شروق تقاس بعشرين دقيقة, غير قابلة للمرونة. وكان لدينا خمسة وتسعون ضيفاً يصلون من أربع مدن، ورحلة منطاد تعتمد على قراءة رياح تؤخذ قبل الإقلاع بثلاث ساعات. لا شيء من ذلك يحتمل الارتجال.',
      intent: 'بنينا العرس حول الضوء لا مخطط القاعة. أقيم عشاء الترحيب في عمق مطعم الكهف في أرغوس ليحمل السقفُ ضوءَ الشموع لا أقواس الزهور. وكانت المراسم لحظة صغيرة مقصودة على الشرفة عند الساعة الذهبية، أبقيت دون ثلاثين دقيقة. وامتد الاستقبال عبر الوادي بينما لانت الصخور إلى الكهرمان. وافتتح اليوم الثالث بإقلاع عند 05:14, الضيوف في السلة، والعروسان في منطاد ثانٍ، والتصوير من ثالث. القوس كله يسع ثلاث ليالٍ بالضبط.',
      captions: [
        'الشروق، لحظة وصول قراءة الرياح.',
        'الوادي بين المراسم والعشاء.',
        'عهود على شرفة لم تحتج زهوراً.',
        'عشاء الترحيب داخل مطعم الكهف.',
        'مائدة وضعت حيث ارتد ضوء الشموع مرة.',
        'العروسان، قبل أن يرتفع المنطاد الثاني.',
        'الاستقبال، بينما تحولت الصخور إلى كهرمان.',
        'آخر ليلة من الليالي الثلاث.',
      ],
      numbers: ['ضيفاً', 'ليالٍ', 'رحلات أعيد حجزها'],
      credits: {
        Venue: 'Argos in Cappadocia',
        Catering: 'مطعم Seki, داخلي لأرغوس',
        AV: 'شريك Audeon Pro في كابادوكيا',
        Florals: 'فريق Aksel Atelier المتنقل',
        Photography: 'قريباً',
        Scenography: 'GlowMeets, داخلي',
      },
    },
  },
};

export const localizeProject = (project, lang) => {
  const overlay = OVERLAYS[lang]?.[project.slug];
  if (!overlay) return project;
  const merged = { ...project, ...overlay };
  if (overlay.captions) {
    merged.essay = project.essay.map((p, i) => ({
      ...p,
      caption: overlay.captions[i] || p.caption,
    }));
    delete merged.captions;
  }
  if (overlay.numbers) {
    merged.numbers = project.numbers.map((n, i) => ({
      ...n,
      label: overlay.numbers[i] || n.label,
    }));
  }
  return merged;
};

export default OVERLAYS;
